// One error model for every API call. Screens branch on `kind`, never on
// raw status codes, and show `message` (plain language, UX-DESIGN.md §6)
// rather than the server's machine code.

export type ApiErrorKind =
  | "network" // fetch itself failed: offline, DNS, proxy down
  | "session" // no/expired/revoked session: sign in again
  | "csrf" // session exists but this tab has no valid CSRF token
  | "step_up_required" // fresh MFA needed and the user declined/failed the prompt
  | "forbidden" // authenticated but not allowed (wrong party type, role)
  | "not_found"
  | "validation" // 400: the request itself was refused
  | "conflict"
  | "unavailable" // 503: feature not configured on this deployment
  | "upstream" // 502: a provider (Stripe) call failed
  | "server" // 5xx
  | "rate_limited" // 429 rate_limited (cmd/core-platform/auth_ratelimit.go deny): wait Retry-After seconds
  | "not_available"; // endpoint does not exist yet (typed stub, API-GAPS.md)

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  /** Machine code from the server's {"error": "..."} body, if any. */
  readonly code: string | undefined;
  /** The idempotency key the failed call carried, for §6's "show the reference ID". */
  readonly idempotencyKey: string | undefined;
  readonly path: string | undefined;
  /**
   * The body's `reason`, when the server gives one: `password_policy`
   * (internal/auth/password_policy.go PasswordPolicyError.Reason) says
   * which rule the password failed.
   */
  readonly reason: string | undefined;
  /** Seconds from the Retry-After header of a 429 (at least 1), if present. */
  readonly retryAfterSeconds: number | undefined;
  /**
   * The body's `detail` object on a typed rejection (G-22):
   * 422 {"error": "<code>", "detail": {...}} from campaign_commands.go
   * rejectionResponse/writeCampaignError (:180-247), refunds.go rejectRefund
   * (0056, :44-49) and publisher_commands.go writePayoutRejection (0057,
   * :675-700). Fresh figures (`minimum`, `max_refundable`, `available`)
   * arrive here as exact decimal strings.
   */
  readonly detail: Record<string, unknown> | undefined;
  /**
   * The body's `detail` when it is a STRING: the staff commands' 409
   * request_rejected, whose detail is the SQL's own "rejected: ..." text
   * with the prefix cut (provider_operations.go:209-214, client_matrix.go:277-284).
   */
  readonly detailText: string | undefined;

  constructor(init: {
    kind: ApiErrorKind;
    status?: number;
    code?: string;
    message?: string;
    idempotencyKey?: string;
    path?: string;
    reason?: string;
    retryAfterSeconds?: number;
    detail?: Record<string, unknown>;
    detailText?: string;
  }) {
    super(init.message ?? messageFor(init.kind, init.code, init.reason));
    this.name = "ApiError";
    this.kind = init.kind;
    this.status = init.status ?? 0;
    this.code = init.code;
    this.idempotencyKey = init.idempotencyKey;
    this.path = init.path;
    this.reason = init.reason;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.detail = init.detail;
    this.detailText = init.detailText;
  }

  /** A string field of `detail` (a money figure, id or timestamp), if present. */
  detailString(name: string): string | undefined {
    const v = this.detail?.[name];
    return typeof v === "string" ? v : undefined;
  }

  /**
   * Whether an identical retry could succeed. For financial actions the UI
   * must still show the reference ID and check status before re-enabling
   * retry (UX-DESIGN.md §6), which is why this is a hint, not a policy.
   */
  get retryable(): boolean {
    return this.kind === "network" || this.kind === "server" || this.kind === "upstream";
  }
}

/** Thrown by typed stubs for endpoints core-platform does not have yet. */
export class NotYetAvailableError extends ApiError {
  readonly endpoint: string;
  readonly gapId: string;
  constructor(endpoint: string, gapId: string) {
    super({ kind: "not_available", message: `Not yet available: ${endpoint} does not exist in core-platform yet.` });
    this.name = "NotYetAvailableError";
    this.endpoint = endpoint;
    this.gapId = gapId;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/**
 * Server error codes -> kind. Codes come from writeAuthErr (auth.go:52-78),
 * writeAuthError (internal/auth/session_middleware.go:164-168), the
 * handlers' own {"error": ...} bodies, and writeRejected (publisher.go:112-115).
 */
const CODE_KIND: Record<string, ApiErrorKind> = {
  session_required: "session",
  session_expired: "session",
  session_revoked: "session",
  csrf_mismatch: "csrf",
  mfa_step_up_required: "step_up_required",
  rate_limited: "rate_limited",
  wrong_party_type: "forbidden",
  insufficient_role: "forbidden",
  // 0062 authority refusals (campaign_commands.go:219-225 rejectStatus → 403).
  insufficient_authority: "forbidden",
  authority_not_yet_active: "forbidden",
  site_not_owned: "forbidden",
  deposits_not_configured: "unavailable",
  stripe_not_configured: "unavailable",
  stripe_checkout_session_failed: "upstream",
  stripe_account_creation_failed: "upstream",
  stripe_account_link_failed: "upstream",
  concurrent_update_retry: "conflict",
  // 0065: 403 from RequireSession (session_middleware.go:95-97), 422 from
  // the SQL's own check (rejectStatus). One meaning either way.
  account_closed: "forbidden",
  // 0065: 410 on the 0023 site-scoped destination route (account_lifecycle.go:175-180).
  endpoint_replaced: "not_found",
};

export function kindFor(status: number, code: string | undefined): ApiErrorKind {
  if (code && CODE_KIND[code]) return CODE_KIND[code];
  if (code && code.endsWith("_not_found")) return "not_found";
  // Credential/ceremony failures (auth.go writeAuthErr) are 401s too, but
  // they mean "that code/password was wrong", not "your session ended".
  if (code === "webauthn_verification_failed") return "validation";
  if (code && code.startsWith("auth: ")) {
    if (code.startsWith("auth: recent mfa verification required")) return "step_up_required";
    if (status === 401 || status === 400) return "validation";
    if (status === 409) return "conflict";
    if (status === 503) return "unavailable";
  }
  if (status === 429) return "rate_limited";
  if (status === 401) return "session";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 502) return "upstream";
  if (status === 503) return "unavailable";
  if (status >= 500) return "server";
  if (status >= 400) return "validation";
  return "server";
}

/** Plain-language copy for specific codes; falls back to the kind's message. */
const CODE_MESSAGE: Record<string, string> = {
  session_expired: "Your session has ended. Sign in again to continue.",
  session_revoked: "You were signed out. Sign in again to continue.",
  csrf_mismatch: "For your security, sign in again in this tab before making changes.",
  wrong_party_type: "This page is for a different account type.",
  deposits_not_configured: "Adding funds isn't available on this deployment yet (payments are not configured).",
  stripe_not_configured: "Payout setup isn't available on this deployment yet (payments are not configured).",
  stripe_checkout_session_failed: "We couldn't start checkout with our payment provider. Nothing was charged. Try again in a moment.",
  stripe_account_creation_failed: "We couldn't reach our payment provider to set up your payout account. Try again in a moment.",
  stripe_account_link_failed: "We couldn't get a fresh onboarding link from our payment provider. Try again in a moment.",
  request_rejected: "The request was refused. Check the details and try again.",
  webauthn_verification_failed: "That security key couldn't be verified. Try again, or use another method.",
  "auth: invalid credentials": "That email and password don't match an account.",
  "auth: invalid mfa code": "That code isn't valid. Codes change every 30 seconds — try the current one.",
  "auth: account disabled": "This account is disabled. Contact support.",
  "auth: invalid or expired authorization code": "Your sign-in attempt expired. Start again.",
  "auth: pkce verification failed": "Your sign-in attempt couldn't be verified. Start again.",
  "auth: redirect_uri not registered for client":
    "This portal's address isn't registered with the sign-in service. (Operator: add it to oauth_clients.redirect_uris — see web/README.md.)",
  "auth: invalid oauth client": "This portal isn't registered with the sign-in service. (Operator: check VITE_OAUTH_CLIENT_ID.)",
  "auth: webauthn is not configured": "Security keys aren't enabled on this deployment. Use an authenticator app code.",
  "auth: webauthn credential label must be 1-64 characters": "Give the key a name of 1–64 characters.",
  "auth: webauthn credential already registered": "That security key is already registered.",
  "auth: cannot remove the last MFA factor; use MFA reset":
    "You can't remove your only verification method. Add another one first.",
  "auth: mfa already confirmed": "An authenticator app is already set up for this account.",
  "auth: mfa not enrolled": "Two-step verification isn't set up for this account yet.",
  "auth: recent mfa verification required for this action": "Confirm it's you to continue.",
  // Account recovery (cmd/core-platform/recovery.go writeRecoveryErr). One
  // error for every unusable token: unknown, expired, used or superseded.
  recovery_token_invalid:
    "This link or code isn't valid any more. It may have expired (they last an hour), been used already, or been replaced by a newer request. Request a new one.",
  recovery_contact_unconfirmed:
    "Enter the code sent to your recovery contact first. Both channels must confirm before two-step verification can be reset.",
  recovery_failed: "Something went wrong on our side. Nothing was changed. Try again in a moment.",
  password_policy: "Choose a different password.",
  rate_limited: "Too many attempts. Wait a moment, then try again.",
  amount_and_idempotency_key_required: "Enter an amount.",
  invalid_amount: "Enter a valid amount greater than zero.",
  domain_required: "Enter a domain.",
  tool_name_and_pricing_model_required: "Enter a tool name and choose a pricing model.",
  // Idempotent portal writes (G-21): campaign_commands.go runCampaignCommand,
  // publisher_commands.go runPublisherCommand (0057), submit_refund (0056),
  // request_payout (0057).
  idempotency_key_reused:
    "This retry doesn't match the request first sent under the same reference, so nothing new was done. Check the current status before trying again.",
  idempotency_key_mismatch: "The request carried two different references. Nothing was done. Try again.",
  concurrent_update_retry: "Someone else changed this at the same moment. Nothing was saved. Try again.",
  invalid_value: "One of the numbers isn't valid. Check the amounts and try again.",
  // Campaign writes (0052; web/API-GAPS.md "Implemented: campaign side").
  campaign_not_found: "We couldn't find that campaign. It may not exist, or it may belong to another account.",
  campaign_ended: "This campaign has ended. Ending is permanent, so it can't be changed any more.",
  invalid_name: "Use a name of 1–120 characters.",
  schedule_end_before_start: "The end must be after the start.",
  schedule_version_conflict: "The schedule was changed somewhere else since you opened it. Reload to see the current dates.",
  lifetime_budget_required: "Enter a lifetime budget.",
  invalid_lifetime_budget: "The lifetime budget must be more than zero.",
  invalid_daily_budget: "The daily budget must be more than zero.",
  daily_budget_exceeds_lifetime: "The daily budget can't be more than the lifetime budget.",
  lifetime_budget_below_bid: "The lifetime budget must be at least one bid.",
  invalid_budget_timezone: "Choose a valid time zone, like America/New_York.",
  invalid_bid_amount: "The bid must be more than zero.",
  budget_below_committed: "That's below what this campaign has already spent or reserved.",
  no_live_release: "This campaign has nothing live yet, so its budget is set on the version you submit.",
  config_version_not_approved: "The targeting and bid version hasn't been approved yet, so nothing was promoted.",
  creative_version_not_approved: "The creative version hasn't been approved yet, so nothing was promoted.",
  terms_acceptance_required: "Accept the current commercial terms for these versions first.",
  terms_reacceptance_required: "The commercial terms changed since you accepted them. Review and accept the new terms to activate.",
  terms_version_changed: "The commercial terms changed while you were reviewing them. Review the new terms and accept again.",
  terms_unavailable_for_currency: "No commercial terms are published for this currency yet.",
  invalid_destination_url: "The destination URL isn't valid. Use a full https:// address with a public domain name.",
  // Refunds (0056, refunds.go / submit_refund).
  refund_exceeds_refundable: "That's more than can be refunded from this deposit right now.",
  insufficient_spendable: "Your wallet's spendable balance can't cover that refund right now.",
  deposit_locked_by_dispute: "This deposit is locked by an open dispute, so it can't be refunded until the dispute is resolved.",
  deposit_not_found: "We couldn't find that deposit on your account.",
  reason_too_long: "Keep the reason to 500 characters.",
  refund_not_found: "We couldn't find that refund on your account.",
  checkout_session_not_found: "We couldn't find that checkout on your account.",
  // Publisher writes and payouts (0057).
  invalid_domain: "Enter a valid domain name, like mcp.example.com.",
  site_domain_already_registered: "You've already registered this domain.",
  invalid_tool_name: "Use a tool name of 1–128 characters, exactly as your server exposes it.",
  slot_tool_name_taken: "This site already has a slot for that tool.",
  site_not_active: "API keys can be created once the site is active (after domain verification).",
  environment_unavailable: "Only live keys can be created. Test keys aren't available yet.",
  invalid_revocation_reason: "Choose why you're revoking this key.",
  api_key_already_revoked: "This key was already revoked.",
  api_key_not_found: "We couldn't find that API key on your account.",
  invalid_legal_name: "Use a legal name of 1–200 characters.",
  legal_name_locked: "Your legal name can't be changed once identity verification has started. Contact support to correct it.",
  unsupported_profile_field: "Only the legal name can be changed here.",
  connect_account_missing: "Set up your payout account before requesting a payout.",
  connect_payouts_disabled: "Our payment provider hasn't enabled payouts on your payout account yet. Continue payout setup.",
  insufficient_claimable_balance: "That's more than is available to pay out right now.",
  // Migration 0074: request_payout's $200 (default) minimum per routine
  // request. detail carries {minimum, payable} as exact decimal strings.
  below_minimum_payout: "Payout requests have a minimum amount.",
  site_not_found: "We couldn't find that site on your account.",
  payout_not_found: "We couldn't find that payout on your account.",
  debt_case_not_found: "We couldn't find that case on your account.",
  // Account (0055, cmd/core-platform/account.go).
  mfa_not_enrolled: "Set up a security key or authenticator app first. Recovery codes come with your first verification method.",
  cross_origin_forbidden: "This page isn't being loaded from the VADS portal's own address, so your session can't be used here.",
  // Targeting, candidates, pre-validation (0061).
  query_must_be_2_to_253_characters: "Type at least 2 characters of a site domain to search.",
  too_many_categories: "Choose at most 64 categories.",
  unknown_category: "One of the chosen categories isn't in the targeting list any more. Reload and choose again.",
  category_not_cleared: "A regulated category you chose hasn't been cleared by the platform yet, so it can't be targeted.",
  invalid_targeting: "The targeting isn't valid: a category can't be both targeted and excluded, or listed twice.",
  unknown_publisher: "One of the excluded publishers no longer has an active site. Remove it and try again.",
  creative_invalid: "The creative doesn't pass validation. Fix the errors shown on the creative step.",
  no_pending_candidate: "There's nothing pending to withdraw any more. The page has been refreshed.",
  candidate_mismatch: "The pending versions changed since this page loaded, so nothing was withdrawn. Check what's pending now.",
  config_version_id_or_creative_version_id_required: "There's no pending version to withdraw.",
  invalid_appeal_basis: "Explain why the decision should be reconsidered, in 1–2,000 characters.",
  version_withdrawn: "That version was withdrawn or replaced, so it can't be appealed or accepted.",
  version_not_rejected: "Only a rejected version can be appealed.",
  version_not_current: "Only the version that's currently pending can be appealed. A newer version has replaced it.",
  appeal_already_filed: "An appeal was already filed for this version. Each version can be appealed once.",
  version_not_found: "We couldn't find that version on this campaign.",
  // Organization (0062, cmd/core-platform/org.go; the codes are the SQL
  // functions' _campaign_reject calls in 0062_organization.up.sql).
  insufficient_role: "Your role in this organization doesn't allow this.",
  insufficient_authority: "Your role doesn't give you authority over this kind of request.",
  authority_not_yet_active:
    "Your authority for this is still in its 72-hour waiting period after being granted, so you can't approve yet.",
  member_not_found: "That person isn't an active member of this organization any more.",
  invitation_not_found: "We couldn't find that invitation.",
  last_owner:
    "This is the organization's last owner. Transfer ownership to another member first; that's the only way to step down.",
  approval_quorum_would_break:
    "That would leave a pending approval with nobody eligible to approve it. Resolve or cancel that request first.",
  cannot_grant_self: "You can't grant a privileged role to yourself. Another owner or admin has to request it.",
  role_change_pending: "A role change for this member is already waiting for approval. Approve or cancel it first.",
  invalid_role: "Choose one of the listed roles.",
  invalid_email: "Enter a valid email address.",
  email_and_role_required: "Enter an email address and choose a role.",
  role_required: "Choose a role.",
  to_user_id_required: "Choose the member who'll become owner.",
  already_member: "That person is already a member of this organization.",
  invitation_already_open: "There's already an open invitation for that address. Revoke it first to send a new one.",
  invitation_already_revoked: "That invitation was already revoked.",
  invitation_already_accepted: "That invitation has already been accepted.",
  invitation_invalid:
    "This invitation link isn't valid any more. It may have expired (invitations last 7 days), been used, or been revoked. Ask an owner or admin to invite you again.",
  token_and_password_required: "Choose a password.",
  email_in_use: "That email address already belongs to another VADS account. Ask for an invitation to a different address.",
  already_owner: "That member is already an owner.",
  api_key_disposition_required: "This member created API keys that are still live. Choose what happens to each one.",
  invalid_api_key_dispositions: "One of the API-key choices isn't valid. Reassign each key to another active member, or revoke it.",
  dual_control_request_not_found: "We couldn't find that request.",
  approver_is_requester: "You made this request, so a different member has to approve it.",
  approver_is_subject: "This request is about you, so a different member has to approve it.",
  dual_control_single_user_fallback:
    "This request uses extended verification instead of a second approver, so there's nothing to approve. It takes effect after its waiting period.",
  dual_control_not_pending: "This request isn't waiting for approval any more.",
  dual_control_not_cancelable: "This request has already finished, so it can't be canceled.",
  invalid_status_filter: "That filter isn't valid.",
  notification_not_found: "We couldn't find that notification.",
  invalid_category: "That category isn't one of the audit log's categories.",
  invalid_actor: "That member filter isn't valid.",
  invalid_cursor: "That page link is out of date. Reload the list.",
  // Statement-line disputes and evidence (0063, disputes.go,
  // dispute_evidence.go; 0063_statement_disputes.up.sql).
  statement_not_found: "We couldn't find that statement on your account.",
  line_not_found: "We couldn't find that line on this statement.",
  dispute_not_found: "We couldn't find that dispute on your account.",
  evidence_not_found: "One of the evidence files wasn't found on your account. Upload it again.",
  evidence_not_clean:
    "One of the evidence files didn't pass the safety check (or hasn't finished it), so it can't be attached. Remove it and try again.",
  evidence_type_not_allowed: "That file type isn't accepted. Use a PDF, PNG, JPEG or plain-text file.",
  evidence_empty: "That file is empty.",
  evidence_too_large: "That file is larger than 2 MB. Upload a smaller file.",
  evidence_storage_cap_exceeded: "Your account has used its 20 MB of evidence storage, so this file can't be added.",
  multipart_form_required: "The file couldn't be sent. Try again.",
  invalid_multipart: "The file couldn't be read. Try again.",
  one_file_per_upload: "Upload one file at a time.",
  file_part_required: "Choose a file to upload.",
  invalid_evidence_id: "One of the evidence references isn't valid. Remove it and upload the file again.",
  dispute_window_closed: "The dispute window for this line has closed, so it can't be disputed.",
  line_already_disputed: "This line has already been disputed. Each line can be disputed once.",
  line_not_disputable: "This line can't be disputed: only billed deliveries can be.",
  reservation_not_disputable: "This delivery has already been reversed or released, so there's nothing left to dispute.",
  dispute_already_open: "There's already an open dispute on this delivery.",
  invalid_reason: "Explain the problem in 1–2,000 characters.",
  too_many_evidence_items: "Attach at most 10 evidence files.",
  publisher_share_claimed:
    "The publisher's share of this delivery has already been claimed by a payout, so it can't be reversed through this dispute.",
  invalid_json: "The request couldn't be read. Try again.",
  // Payout destination, closure and export (0065, account_lifecycle.go;
  // the SQL's _campaign_reject codes in 0065_account_closure_payout_destination.up.sql).
  account_closed: "This account is closed. Only its issued statements are still available, read-only.",
  endpoint_replaced: "This way of changing the payout destination was replaced. Use Payout setup → Payout destination.",
  no_current_destination:
    "There's no payout account to replace yet. Set up your first payout account with Stripe above; only a replacement needs a second approver.",
  destination_change_pending:
    "A payout destination change is already waiting. It has to take effect or be canceled before another can be requested.",
  invalid_account_id: "Our payment provider returned an account we couldn't use. Nothing was changed. Try again.",
  destination_unchanged: "That's already your payout account, so nothing was changed.",
  account_already_registered: "That payout account is already registered on VADS, so it can't be used again.",
  idempotency_key_required: "The request was missing its reference. Nothing was done. Try again.",
  confirmation_required: "Confirm that you want to close the account.",
  closure_blocked: "The account can't be closed yet: something still needs to be resolved. The list below is up to date.",
  export_too_large:
    "Your records are larger than the 8 MiB the export can return in one download, so no file was produced. Contact support for a copy.",
  // Publisher inventory: declared tools, exclusions, slot lifecycle
  // (migration 0066, cmd/core-platform/publisher_inventory.go).
  tools_required: "Declare at least one tool.",
  invalid_tool_declaration: "One of the declared tools isn't valid. Check the name, description and category.",
  duplicate_tool_name: "Two declared tools share the same name. Each tool name must be unique on this site.",
  too_many_tools: "Declare at most 50 tools.",
  invalid_locale: "Enter a valid locale, like en, en-US or zh-Hant-TW.",
  invalid_advertiser_id: "One of the excluded advertiser ids isn't valid.",
  too_many_exclusions: "That list is too long: at most 500 entries.",
  no_change: "That's already this slot's live version, so nothing was created.",
  slot_retired: "This slot was retired, so it can't be changed.",
  pricing_model_or_tool_name_required: "Choose a pricing model, a new tool name, or both.",
  invalid_pricing_model: "Choose CPM or CPE.",
  slot_version_not_pending: "This version isn't waiting for review any more.",
  slot_version_not_found: "We couldn't find that slot version.",
  slot_not_found: "We couldn't find that slot on your account.",
  slot_already_paused: "This slot is already paused.",
  slot_not_paused: "This slot isn't paused, so it can't be resumed.",
  // Receivable repayment (migration 0067, cmd/core-platform/receivable_collection.go).
  invalid_case_id: "That isn't a valid case id.",
  receivable_not_found: "We couldn't find that receivable on your account.",
  receivable_not_collectible: "This case can't be paid right now (an open chargeback, or nothing left to collect).",
  nothing_outstanding: "There's nothing outstanding on this case any more.",
  amount_exceeds_outstanding: "That's more than is outstanding on this case.",
  payments_not_configured: "Paying this down isn't available on this deployment yet (payments are not configured).",
  // Financial quarantine (migration 0072): request_payout's trigger while
  // the account (or the platform ledger) is under a financial review.
  financial_quarantine:
    "Payouts are paused while we review this account's financial records. Your earnings aren't lost; you can request the payout once the review is complete.",
};

/**
 * A TOTP code can be used once (auth.go writeAuthErr, :97): a replayed code
 * gets the same 401 "auth: invalid mfa code" as a wrong one, so the portal
 * can't tell them apart. This is the hint it shows under that error, never
 * a claim that the code *was* reused.
 */
export function totpReuseHint(err: unknown): string | null {
  return err instanceof ApiError && err.code === "auth: invalid mfa code"
    ? "If you just used this code (to sign in or confirm another action), wait for the next one — each code works only once."
    : null;
}

const KIND_MESSAGE: Record<ApiErrorKind, string> = {
  network: "We couldn't reach VADS. Check your connection and try again.",
  session: "Your session has ended. Sign in again to continue.",
  csrf: "For your security, sign in again in this tab before making changes.",
  step_up_required: "This action needs you to confirm it's you. It was not submitted.",
  forbidden: "You don't have access to do that.",
  not_found: "We couldn't find that. It may not exist, or it may belong to another account.",
  validation: "The request was refused. Check the details and try again.",
  conflict: "That conflicts with the current state. Refresh and try again.",
  unavailable: "This feature isn't available on this deployment yet.",
  upstream: "Our payment provider didn't respond as expected. Try again in a moment.",
  server: "Something went wrong on our side. Try again in a moment.",
  rate_limited: "Too many attempts. Wait a moment, then try again.",
  not_available: "Not yet available.",
};

/**
 * password_policy reasons (internal/auth/password_policy.go), checked
 * identically at signup and at password reset.
 */
export type PasswordPolicyReason =
  | "too_short"
  | "too_long"
  | "common_password"
  | "matches_email"
  | "repeated_character"
  | "invalid_characters";

export const PASSWORD_POLICY_MESSAGE: Record<PasswordPolicyReason, string> = {
  too_short: "Use at least 15 characters.",
  too_long: "That password is too long. Use at most 72 bytes (72 plain letters; accented letters and emoji use more).",
  common_password: "That password is too common, or has appeared in data breaches. Choose a different one.",
  matches_email: "Don't use your email address, or the part before the @, as your password.",
  repeated_character: "Don't use one character repeated. Try a few unrelated words.",
  invalid_characters:
    "That password contains characters we can't accept, such as control characters. Use letters, numbers, spaces and symbols.",
};

export function messageFor(kind: ApiErrorKind, code?: string, reason?: string): string {
  if (code === "password_policy" && reason) {
    const policy = (PASSWORD_POLICY_MESSAGE as Record<string, string | undefined>)[reason];
    if (policy) return policy;
  }
  if (code) {
    if (CODE_MESSAGE[code]) return CODE_MESSAGE[code];
    // writeAuthErr returns err.Error(), which may carry a wrapped suffix
    // ("auth: invalid or expired authorization code: <detail>").
    for (const [known, msg] of Object.entries(CODE_MESSAGE)) {
      if (known.startsWith("auth: ") && code.startsWith(known)) return msg;
    }
  }
  return KIND_MESSAGE[kind];
}
